// Copyright (c) 2019 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import * as React from "react";
import { connect } from "react-redux";
import { FormattedMessage, defineMessages } from "react-intl";

import { RoutedProps, withRouter } from "view/reactrouterinterceptor";
import { ActionType, IAction, IActionSortTable, raiseAction } from "actions";
import { IModule } from "shared/types/parameter";
import { isSet } from "shared/utils/utils";
import { makeSortHeader } from "guiutils";
import { IState } from "states/states";
import { ITableSort } from "states/guistates";

import Module from "view/comp/Module";

// sort key of table
const TABLE_NAME = "t_modules";

interface IModuleListProps {
  modules: IModule[];
  moduleState: boolean[];
  tableSort: ITableSort | undefined;
  showId: boolean;
  filterText?: string;
}

const mapStateToProps = (state: IState) => {
  const curUser = state.site.userList?.find((curItem) => curItem.secId === state.site.user?.secId);
  const props: IModuleListProps = {
    modules: state.para.parameter?.module ?? [],
    moduleState: state.data.module,
    tableSort: state.gui.tableSort[TABLE_NAME],
    showId: Boolean(curUser?.accessRights.setVirtualParameters),
    filterText: state.gui.searchFilterText,
  };
  return props;
};

interface IModuleListDispatch {
  onSort: (sort: IActionSortTable) => void;
}

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: IModuleListDispatch = {
    onSort: (sort: IActionSortTable) => {
      dispatch(raiseAction(ActionType.ACT_SORT_TABLE, sort));
    },
  };
  return dispatchProps;
};

class ModuleListComponent extends React.Component<
  IModuleListProps & IModuleListDispatch & RoutedProps
> {
  private getModuleList() {
    const filterTextLowerCase = this.props.filterText?.toLocaleLowerCase();
    const sortCol = (this.props.tableSort?.column ?? "name") as keyof IModule | "value";

    // get list of used modules
    const allList = Array.from(Array(this.props.modules.length).keys());
    const filteredList = allList.filter((curIndex) => {
      const item = this.props.modules[curIndex];
      return (
        item.webActive &&
        (!isSet(filterTextLowerCase) || item.name.toLocaleLowerCase().includes(filterTextLowerCase))
      );
    });
    const sortedList = filteredList.sort((a, b) => {
      const keyA =
        sortCol === "value" ? this.props.moduleState[a] : this.props.modules[a]?.[sortCol!];
      const keyB =
        sortCol === "value" ? this.props.moduleState[b] : this.props.modules[b]?.[sortCol!];
      const sort =
        (isSet(keyA) && isSet(keyB) && keyA > keyB) || (isSet(keyA) && !isSet(keyB)) ? 1 : -1;
      return (this.props.tableSort?.isSortAscending !== false ? sort : -sort) as number;
    });
    return sortedList;
  }

  public render() {
    const indexList = this.getModuleList();
    const showModuleComment = indexList.some((curIndex) =>
      Boolean(this.props.modules[curIndex].comment),
    );
    const items = indexList.map((curItem) => {
      return <Module key={curItem} index={curItem} showModuleComment={showModuleComment} />;
    });

    return (
      <div id="modules" className="flex-self flex-content mb-3">
        <div className="tableSearchHeader mx-2">
          <h1>
            <FormattedMessage id="Modules" />
          </h1>
        </div>
        {items.length ?
          <table className="datatable fullwidthwhite">
            <thead>
              <tr>
                {this.props.showId && <th className="optional-lg">Id</th>}
                {makeSortHeader(
                  TABLE_NAME,
                  "name",
                  <FormattedMessage id="Name" />,
                  this.props.tableSort ?? { column: "name", isSortAscending: true },
                  this.props.onSort,
                )}
                {makeSortHeader(
                  TABLE_NAME,
                  "type",
                  <FormattedMessage id="Type" />,
                  this.props.tableSort,
                  this.props.onSort,
                  ["optional-md"],
                )}
                {makeSortHeader(
                  TABLE_NAME,
                  "address",
                  <FormattedMessage id="Address" />,
                  this.props.tableSort,
                  this.props.onSort,
                  ["optional-lg"],
                )}
                <th>
                  <FormattedMessage id="Usage" />
                </th>
                {makeSortHeader(
                  TABLE_NAME,
                  "value",
                  <FormattedMessage id="Status" />,
                  this.props.tableSort,
                  this.props.onSort,
                  ["value-col"],
                )}
                {showModuleComment &&
                  makeSortHeader(
                    TABLE_NAME,
                    "comment",
                    <FormattedMessage id="Comment" />,
                    this.props.tableSort,
                    this.props.onSort,
                    ["optional-lg"],
                  )}
              </tr>
            </thead>
            <tbody>{items}</tbody>
          </table>
        : <FormattedMessage id="No modules present." />}
      </div>
    );
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(ModuleListComponent));
